import React, { FC, useCallback } from 'react';
import { getPathToElement } from '@shared/helpers/recursive-element.helpers';
import { useToggleState } from '@client/hooks/use-utils.hooks';
import SaveRecipeConfirmDialog from './SaveRecipeConfirmDialog';
import { IElement } from '@shared/models/project.model';
import {
  Box,
  Breadcrumbs,
  List,
  ListItem,
  Switch,
  Typography,
} from '@mui/material';
import { Recipe } from '@shared/models/recipe.model';
import { getProject } from '@client/store/project';
import { useApplyBaselineRecipeOverwrite } from '@client/hooks/use-apply-baseline-recipe-overwrite.hook';
import {
  useAllRecipeElementsInProject,
  useUpdateRecipeFromElement,
} from '@features/recipes/use-recipes.hooks';
import { useElementResults } from '@features/results/use-results.hooks';
import { getElementName } from '@shared/helpers/element.helpers';

interface UpdateRecipesDialogProps {
  element: IElement;
  recipe: Recipe;
  visible: boolean;
  hideDialog: () => void;
}

const UpdateRecipesDialog: FC<UpdateRecipesDialogProps> = ({
  element,
  recipe,
  visible,
  hideDialog,
}) => {
  const project = getProject();
  const elementsWithRecipe = useAllRecipeElementsInProject(recipe);
  const otherElementsWithRecipe = useAllRecipeElementsInProject(recipe).filter(
    (recipeElement) => recipeElement.id !== element.id,
  );
  const applyBaselineRecipeOverwrite = useApplyBaselineRecipeOverwrite();
  const updateRecipeFromElement = useUpdateRecipeFromElement();
  // Full element totals. The chart's lifecycle filter must not shrink what Save persists.
  const elementResults = useElementResults(element, undefined, false);

  const [shouldUpdateAllElements, setShouldUpdateAllElements] =
    useToggleState(true);

  const handleRecipeUpdate = useCallback(
    (confirm: boolean) => {
      if (confirm) {
        updateRecipeFromElement(recipe, element, elementResults)
          .then(async (updatedRecipe) => {
            const targets = shouldUpdateAllElements
              ? elementsWithRecipe
              : [element];

            await applyBaselineRecipeOverwrite(
              targets.map((el) => ({
                elementId: el.id,
                recipe: updatedRecipe,
              })),
              {
                snackbar: {
                  type: 'undo',
                  message: 'Recipe saved',
                },
              },
            );
          })
          .catch((error) => console.error(error));
      }
      hideDialog();
    },
    [
      applyBaselineRecipeOverwrite,
      element,
      elementsWithRecipe,
      hideDialog,
      recipe,
      shouldUpdateAllElements,
      elementResults,
      updateRecipeFromElement,
    ],
  );

  return (
    <SaveRecipeConfirmDialog
      open={visible}
      onClose={handleRecipeUpdate}
      title={`Save recipe "${recipe.name}"`}
      confirmText="Save recipe"
      body={
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {`This recipe is in use on ${
              otherElementsWithRecipe.length
            } additional ${
              otherElementsWithRecipe.length > 1 ? 'elements' : 'element'
            } in the project:`}
          </Typography>
          <List>
            {otherElementsWithRecipe.map((element) => {
              const path = getPathToElement(project, element).map((parent) =>
                getElementName(parent),
              );
              return (
                <ListItem key={element.id}>
                  <Breadcrumbs>
                    {[...path, element.name || element.fallbackName].map(
                      (crumb, index) => (
                        <Typography key={index} variant="body2">
                          {crumb}
                        </Typography>
                      ),
                    )}
                  </Breadcrumbs>
                </ListItem>
              );
            })}
          </List>
        </Box>
      }
      option={{
        element: (
          <Switch
            checked={shouldUpdateAllElements}
            onChange={setShouldUpdateAllElements}
          />
        ),
        label: 'Update other elements using this recipe ',
      }}
    />
  );
};

export default UpdateRecipesDialog;
